import React, { useEffect, useState, useCallback } from 'react';
import { getClient } from '../lib/api';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Mail, Phone, MapPin, Building2 } from 'lucide-react';

const BUY_TYPE_COLORS = { CPM: '#2563EB', CPC: '#7C3AED', CPS: '#059669', CPV: '#D97706', CPCV: '#DC2626', CPA: '#0891B2', CPL: '#4F46E5' };
const CURRENCY_SYMBOLS = { USD: '$', INR: '\u20B9', MYR: 'RM', AED: 'AED', SGD: 'S$' };

const STATUS_COLORS = { active: '#10B981', draft: '#71717A', paused: '#F59E0B', completed: '#3B82F6' };

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchClient = useCallback(async () => {
    try {
      const { data } = await getClient(id);
      setClient(data);
    } catch {
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchClient(); }, [fetchClient]);

  if (loading) return <div className="h-64 bg-zinc-100 animate-pulse" data-testid="client-detail-loading" />;
  if (!client) return null;

  const campaigns = client.campaigns || [];
  const payments = client.payments || [];
  const totalBudget = campaigns.reduce((s, c) => s + (c.budget || 0), 0);
  const totalPaid = payments.filter(p => p.status === 'paid').reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <Button variant="ghost" onClick={() => navigate('/clients')} className="gap-2 text-zinc-600 hover:text-zinc-900 rounded-sm" data-testid="back-to-clients">
        <ArrowLeft size={16} /> Back to Clients
      </Button>

      {/* Header */}
      <div className="bg-white border border-zinc-200 p-6">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="w-14 h-14 bg-zinc-950 text-white flex items-center justify-center text-xl font-bold">
            {client.name?.charAt(0)?.toUpperCase()}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">{client.name}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-zinc-500">
              <span className="flex items-center gap-1"><Building2 size={14} /> {client.company}</span>
              <span className="flex items-center gap-1"><Mail size={14} /> {client.email}</span>
              {client.phone && <span className="flex items-center gap-1"><Phone size={14} /> {client.phone}</span>}
              {client.address && <span className="flex items-center gap-1"><MapPin size={14} /> {client.address}</span>}
            </div>
          </div>
          <span className={`px-3 py-1 text-xs font-medium ${client.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600'}`}>
            {client.status}
          </span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-zinc-200">
          <div>
            <div className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Campaigns</div>
            <div className="font-mono text-2xl font-bold text-zinc-900 mt-1">{campaigns.length}</div>
          </div>
          <div>
            <div className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Total Budget</div>
            <div className="font-mono text-2xl font-bold text-zinc-900 mt-1">${totalBudget.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Payments</div>
            <div className="font-mono text-2xl font-bold text-zinc-900 mt-1">{payments.length}</div>
          </div>
          <div>
            <div className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Total Paid</div>
            <div className="font-mono text-2xl font-bold text-emerald-600 mt-1">${totalPaid.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Campaigns */}
      <div className="bg-white border border-zinc-200 p-6" data-testid="client-campaigns">
        <h3 className="text-lg font-semibold tracking-tight text-zinc-900 mb-4">Campaigns</h3>
        {campaigns.length > 0 ? (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Name</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Buy Type</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Status</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Budget</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Volume</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Price</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Currency</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => {
                const sym = CURRENCY_SYMBOLS[c.currency] || c.currency || '$';
                return (
                <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-zinc-900">{c.name}</td>
                  <td className="px-4 py-3"><span className="text-xs font-bold px-2 py-0.5" style={{ backgroundColor: (BUY_TYPE_COLORS[c.buy_type] || '#71717A') + '18', color: BUY_TYPE_COLORS[c.buy_type] || '#71717A' }}>{c.buy_type || '-'}</span></td>
                  <td className="px-4 py-3"><span className="text-xs font-medium px-2 py-0.5" style={{ backgroundColor: (STATUS_COLORS[c.status] || '#71717A') + '20', color: STATUS_COLORS[c.status] || '#71717A' }}>{c.status}</span></td>
                  <td className="px-4 py-3 text-right font-mono">{sym}{(c.budget || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-mono">{(c.volume || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-mono">{sym}{(c.price || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-500">{c.currency || 'USD'}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-zinc-400 py-6 text-center">No campaigns for this client</p>
        )}
      </div>

      {/* Payments */}
      <div className="bg-white border border-zinc-200 p-6" data-testid="client-payments">
        <h3 className="text-lg font-semibold tracking-tight text-zinc-900 mb-4">Payments</h3>
        {payments.length > 0 ? (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Invoice</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Amount</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Status</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Date</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Method</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-zinc-900">{p.invoice_number || '-'}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">${(p.amount || 0).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 ${p.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : p.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-600">{p.payment_date || '-'}</td>
                  <td className="px-4 py-3 text-zinc-600">{p.method || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-zinc-400 py-6 text-center">No payments for this client</p>
        )}
      </div>
    </div>
  );
}
